<script setup lang="ts">
import { isProjectSensitiveKey, sensitiveFieldsKey } from '@/utils/sensitive-fields'
import AttachmentController from '@controllers/AttachmentController'
import { router } from '@inertiajs/vue3'
import { filesize } from 'filesize'
import Select from 'primevue/select'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { ExtendedEnum } from '../../types'
import AttachmentData = App.Data.AttachmentData
import CompanyDocumentType = App.Enums.DocumentTypes.CompanyDocumentType
import NaturalPersonDocumentType = App.Enums.DocumentTypes.NaturalPersonDocumentType
import EstateObjectDocumentType = App.Enums.DocumentTypes.EstateObjectDocumentType

const { t, locale } = useI18n()

const props = withDefaults(
  defineProps<{
    documents?: AttachmentData[]
    resource?: 'company' | 'natural-person' | 'estate-object'
    resourceId?: string | number
    documentTypes?: ExtendedEnum<CompanyDocumentType | NaturalPersonDocumentType | EstateObjectDocumentType>[]
    readOnly?: boolean
  }>(),
  {
    readOnly: false,
  },
)

const localizedDocumentTypes = computed(() =>
  (props.documentTypes ?? []).map((documentType) => ({
    ...documentType,
    label: props.resource ? t(`enums.documentType.${props.resource}.${documentType.id}`) : documentType.id,
  })),
)

const sensitiveFields = inject(
  sensitiveFieldsKey,
  computed(() => new Set<string>()),
)

const isProjectSensitive = inject(
  isProjectSensitiveKey,
  computed(() => false),
)

const sensitivePlaceholderClass = 'bg-surface-300/70 inline-block h-4 w-32 rounded align-middle blur-[1px]'

function shouldBlurField(field: string) {
  return isProjectSensitive.value && sensitiveFields.value.has(field)
}

function getDownloadUrl(attachment: AttachmentData) {
  return attachment.id ? AttachmentController.download(attachment.id).url : undefined
}

function getFileIcon(attachment: AttachmentData) {
  const fileType = [attachment.mime_type, attachment.extension, attachment.filename]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (fileType.includes('pdf')) return 'pi pi-file-pdf text-danger'
  if (fileType.includes('image') || fileType.match(/\b(jpe?g|png|gif|webp|svg)\b/)) return 'pi pi-image text-action'
  if (fileType.includes('excel') || fileType.match(/\b(xls|xlsx|csv)\b/)) return 'pi pi-file-excel text-success-800'
  if (fileType.includes('word') || fileType.match(/\b(doc|docx)\b/)) return 'pi pi-file-word text-action'
  if (fileType.includes('video') || fileType.match(/\b(mp4|mov|avi|webm)\b/)) return 'pi pi-video text-purple-500'

  return 'pi pi-file text-surface-500'
}

function updateCategory(attachmentData: AttachmentData, description: string) {
  if (props.readOnly || !attachmentData.id) {
    return
  }

  router.patch(AttachmentController.update(attachmentData.id).url, { description }, { preserveScroll: true })
}

function formatFileSize(size?: number | null) {
  return size == null ? '' : filesize(size, { locale: locale.value })
}

function formatUpdatedAt(updatedAt?: string | null) {
  return updatedAt ? new Date(updatedAt).toLocaleString(locale.value, { dateStyle: 'short', timeStyle: 'short' }) : ''
}
</script>

<template>
  <DataTable :value="documents">
    <Column field="filename" :header="t('filename')">
      <template #body="{ data }: { data: AttachmentData }">
        <div class="flex items-center gap-2">
          <i :class="getFileIcon(data)" aria-hidden="true" />
          <component :is="getDownloadUrl(data) ? 'a' : 'span'" :href="getDownloadUrl(data)">
            <span v-if="shouldBlurField('AttachmentData.filename')" :class="sensitivePlaceholderClass">&nbsp;</span>
            <template v-else>{{ data.filename }}</template>
          </component>
        </div>
      </template>
    </Column>
    <Column field="mime_type" :header="t('description')">
      <template #body="{ data }: { data: AttachmentData }">
        <Select
          v-if="!readOnly"
          v-model="data.description"
          :options="localizedDocumentTypes"
          option-value="id"
          option-label="label"
          :placeholder="t('types.not_classified')"
          filter
          @change="updateCategory(data, data.description ?? '')" />
        <span v-else-if="shouldBlurField('AttachmentData.description')" :class="sensitivePlaceholderClass">&nbsp;</span>
        <span v-else>
          {{ data.description || data.type || t('types.not_classified') }}
        </span>
      </template>
    </Column>
    <Column :header="t('size')">
      <template #body="{ data }: { data: AttachmentData }">
        {{ formatFileSize(data.size) }}
      </template>
    </Column>
    <Column field="updated_at" :header="t('created_at')">
      <template #body="{ data }: { data: AttachmentData }">
        {{ formatUpdatedAt(data.updated_at) }}
      </template>
    </Column>
    <Column>
      <template #body="{ data }: { data: AttachmentData }">
        <div class="flex justify-end gap-2">
          <a v-if="getDownloadUrl(data) && !isProjectSensitive" :href="getDownloadUrl(data)">
            <Button severity="secondary" size="small" icon="pi pi-download" :label="t('download')" />
          </a>
          <Link
            v-if="!readOnly && data.id && !isProjectSensitive"
            :href="AttachmentController.destroy(data.id)"
            preserve-scroll>
            <Button severity="secondary" size="small" icon="pi pi-trash" :label="t('delete')" />
          </Link>
        </div>
      </template>
    </Column>
    <template #footer>
      <div class="flex justify-end">
        <a
          v-if="resource && resourceId"
          :href="AttachmentController.bulkDownload({ resourceType: resource, resourceId }).url">
          <Button icon="pi pi-folder" :label="t('download_zip')" />
        </a>
      </div>
    </template>
    <template #empty>
      {{ t('no_documents') }}
    </template>
  </DataTable>
</template>

<i18n>
{
  "de": {
    "filename": "Dateiname",
    "description": "Beschreibung",
    "size": "Größe",
    "created_at": "Hochgeladen am",
    "types": {
      "not_classified": "Nicht klassifiziert"
    },
    "instructions": "Ziehen Sie Ihre Dateien hierher oder klicken Sie, um sie auszuwählen.",
    "download": "Herunterladen",
    "download_zip": "Alle als ZIP-Datei herunterladen",
    "delete": "Löschen",
    "no_documents": "Keine Dokumente vorhanden"
  },
  "en": {
    "filename": "Filename",
    "description": "Description",
    "size": "Size",
    "created_at": "Uploaded at",
    "types": {
      "not_classified": "Not classified"
    },
    "instructions": "Drag and drop your files here or click to select them.",
    "download": "Download",
    "download_zip": "Download all as ZIP file",
    "delete": "Delete",
    "no_documents": "No documents available"
  }
}

</i18n>
